import { Injectable, Type } from '@angular/core';
import { Media, POSType, Shop } from '@digilize/shared/definitions/src';
import { ShopToneService } from '@digilize/shared/feature/modules/i18n/src/lib/shop-tone.service';
import { AppTheme } from '@plys/src/application/app.context';
import { PosService } from '@plys/src/infrastructure/pos.service';
import { SeatSelectionType } from '@plys/src/models/checkout';
import { BehaviorSubject, map, Observable, of, Subject, tap } from 'rxjs';

import { ResponseError } from '../../models/response-error';
import { allowedSubscriptionTypesForPos, isRecurringSubscription } from '../../models/subscription';
import {
  AddonsStep,
  InformationStep,
  PaymentStep,
  ReservingStep,
  SeatsSelectionByTicketsStep,
  SeatsSelectionStep,
  Step,
  SubscriptionConfirmationStep,
  SubscriptionCreateAccountStep,
  SubscriptionPeriodSelectionStep,
  SubscriptionRecurringSelectionStep,
  SubscriptionSelectionStep,
  SupplementsStep,
  TicketProductAssigmentStep,
  TicketsSelectionStep,
} from '../../models/step';

export const SEATS_ALREADY_RESERVED_ERROR = 'seat_already_reserved';
export const SEAT_GAP_NOT_ALLOWED_ERROR = 'seat_gap_not_allowed';

/** Order confirmation inside the subscription checkout — a route of that flow, but not a {@link Step}. */
const SUBSCRIPTION_ORDER_ROUTE = 'subs-order';

@Injectable({
  providedIn: 'root',
})
export class CheckoutState {
  private error = new Subject<string | null>();
  private seatSelectionType = new BehaviorSubject<SeatSelectionType>(null);
  private _allowReservations = new BehaviorSubject<boolean>(false);
  readonly allowReservations$ = this._allowReservations.asObservable();
  private _checkoutCancelConfirmation = new BehaviorSubject<boolean>(true);
  checkoutCancelConfirmation$ = this._checkoutCancelConfirmation.asObservable();
  get checkoutCancelConfirmation() {
    return this._checkoutCancelConfirmation.value;
  }
  set checkoutCancelConfirmation(checkoutCancelConfirmation: boolean) {
    this._checkoutCancelConfirmation.next(checkoutCancelConfirmation);
  }
  private _steps = new BehaviorSubject<Step[]>(null);
  steps$ = this._steps.asObservable();
  get steps() {
    return this._steps.value;
  }
  set steps(steps: Step[]) {
    this._steps.next(steps);
  }
  readonly ticketsFirstFlow: Step[] = [
    new TicketsSelectionStep(),
    new SeatsSelectionByTicketsStep(),
    new SupplementsStep(),
    new ReservingStep(),
    new AddonsStep(),
    new InformationStep(),
    new PaymentStep(),
  ];

  readonly seatsFirstFlow: Step[] = [
    new SeatsSelectionStep(),
    new TicketProductAssigmentStep(),
    new SupplementsStep(),
    new ReservingStep(),
    new AddonsStep(),
    new InformationStep(),
    new PaymentStep(),
  ];

  /**
   * Recurring subscriptions: the period step picks the plan and its billing period, and
   * `SubscriptionRecurringSelectionStep` follows as the step that confirms that choice and its
   * quantity before the account step. Dropping the second step sent the buyer from period-selection
   * straight into the login step, which is not the intended flow.
   *
   * It is deliberately *not* the prepaid flow's `SubscriptionSelectionStep`: that one is a catalogue
   * where a plan can still be swapped and the quantity taken back to zero, neither of which applies
   * once a billing period has been chosen and reserved.
   */
  readonly subscriptionSelectionFlow: Step[] = [
    new SubscriptionPeriodSelectionStep(),
    new SubscriptionRecurringSelectionStep(),
    new SubscriptionCreateAccountStep(),
    new SubscriptionConfirmationStep(),
    new PaymentStep(),
  ];

  /**
   * Non-recurring subscriptions (passepartout, punchcard) are paid once, so the period step has
   * nothing to ask them: there are no billing periods, its grid would render empty and its continue
   * button could never be enabled.
   *
   * The start-date step does belong here (PAUL-3143). A punchcard's and a passepartout's validity
   * period is counted from its start date too, so a product whose `start_date_requirement` asks for
   * one has to be datable in the shop that sells it — the backend refuses the order otherwise
   * (`subscription_start_date_required`), and before this there was no field anywhere to answer it
   * with. Like in the recurring flow the step only opens when a reserved line asks for a date; while
   * it stays skipped the account step submits the cart lines itself, see
   * `SubscriptionCreateAccountPageComponent`.
   */
  readonly prepaidSelectionFlow: Step[] = [
    new SubscriptionSelectionStep(),
    new SubscriptionCreateAccountStep(),
    new SubscriptionConfirmationStep(),
    new PaymentStep(),
  ];

  constructor(private readonly posService: PosService, private readonly shopToneService: ShopToneService) {}

  get firstStep(): Step | null {
    return this.steps ? this.steps[0] : null;
  }

  get isFirstStep$(): Observable<boolean> {
    return of(!this.findPreviousStep());
  }

  get onError$(): Observable<string | null> {
    return this.error.asObservable();
  }

  get seatSelectionType$(): Observable<SeatSelectionType> {
    return this.seatSelectionType.asObservable();
  }

  moveReservingStepAsNextStep(cartId: string): void {
    const indexOfNextStep = this.currentStepIndex() + 1;
    this.moveReservingStepToIndexPosition(indexOfNextStep, cartId);
  }

  moveReservingStepBeforePaymentStep(cartId: string): void {
    const paymentStepIndex = this.steps.findIndex((step) => step instanceof PaymentStep);
    this.moveReservingStepToIndexPosition(paymentStepIndex - 1, cartId);
  }

  moveReservingStepToIndexPosition(indexToMove: number, cartId: string): void {
    const reservingStepIndex = this.steps.findIndex((step) => step instanceof ReservingStep);

    if (reservingStepIndex !== -1 && reservingStepIndex !== indexToMove) {
      const [reservingStep] = this.steps.splice(reservingStepIndex, 1);
      this.steps.splice(indexToMove, 0, reservingStep);
      this._steps.next(this.steps);
      localStorage.setItem(`${cartId}-reservingStep`, indexToMove.toString());
    }
  }

  /**
   * The POS read, which now carries the whole shop with it — branding, colours and capability flags
   * included — so the checkout no longer follows it with a `GET /v1.0/shops/{id}` of its own.
   */
  fetchPosById(id: string): Observable<PosContext> {
    return this.posService.getPosById(id).pipe(
      map((response) => {
        return {
          seatSelectionType: response.seat_selection_type,
          theme: response.color_scheme,
          seatsLimit: response.seats_limit_enabled ? response.seats_limit : -1,
          orderTimeLimit: response.order_time_limit_enabled ? response.order_time_limit : -1,
          shopId: response.shop_id,
          shop: response.shop,
          productIds: response.product_ids,
          disableSeatsValidation: response.disable_seat_validation,
          allowReservations: response.allow_reservations ?? false,
          posType: response.type,
          faviconLight: response.favicon_light,
          faviconDark: response.favicon_dark,
        };
      }),
      tap((data) => {
        this.seatSelectionType.next(data.seatSelectionType);
        this._allowReservations.next(data.allowReservations);
        /* The register this cinema addresses its customers in. Both checkout wrappers reach the shop
           through here, so this is the one place the webshop learns it. A POS served without its
           shop by an older backend reads as formal, like every shop that has not opted out. */
        this.shopToneService.setTone(data.shop?.tone);
      })
    );
  }

  setTicketsSteps(seatSelectionType: SeatSelectionType): void {
    this.steps = seatSelectionType === SeatSelectionType.TICKETS_FIRST ? this.ticketsFirstFlow : this.seatsFirstFlow;
  }

  /**
   * Picks the subscription flow for the POS. Driven by the same POS-type rule that filters the
   * product list, so the steps can never disagree with what the shop is able to sell: a shop that
   * cannot offer a recurring subscription has no billing period to choose.
   */
  setSubscriptionSteps(posType: POSType): void {
    const allowedTypes = allowedSubscriptionTypesForPos(posType);
    const canSellRecurring = allowedTypes === null || allowedTypes.some(isRecurringSubscription);
    const flow = canSellRecurring ? this.subscriptionSelectionFlow : this.prepaidSelectionFlow;
    // Both flows are singletons that outlive a checkout, and a `?billing=` link marks the period
    // step skipped on the shared instance — a later bootstrap must not inherit that decision.
    flow.forEach((step) => (step.skipped = false));
    this.steps = flow;
    // The start-date step starts out closed in both flows and is opened by the wrapper as soon as a
    // line asks for a date (`startDateStepNeeded$`): an empty cart asks for none, and a step that
    // opened first and closed a moment later would flicker through the flow the buyer is shown.
    this.setStepSkipped(SubscriptionConfirmationStep, true);
  }

  /**
   * True on `subs-order/:orderId`, reached either in-flow or by a payment-provider redirect landing on
   * `/order/:orderId` first. The order already exists there, so the wrapper must not force the first
   * step: that drops `cart_id` with `replaceUrl` and hides the confirmation for the order that was
   * just paid for. {@link getStepRoute} cannot answer this — it returns the order id.
   */
  isSubscriptionOrderRoute(): boolean {
    return window.location.pathname.split('/').filter(Boolean).includes(SUBSCRIPTION_ORDER_ROUTE);
  }

  /** Whether the active flow walks this step at all — false when it is absent or skipped. */
  hasStep(step: Type<Step>): boolean {
    return (this.steps ?? []).some((s) => s instanceof step && !s.skipped);
  }

  setStepSkipped(step: Type<Step>, skipped: boolean): void {
    // No flow yet means no step to mark — the POS read is what picks one.
    const index = this.steps?.findIndex((s) => s instanceof step) ?? -1;
    if (index !== -1) {
      this.steps[index].skipped = skipped;
    }
  }

  goToNextStep(): Step | undefined {
    return this.findNextStep();
  }

  goToPreviousStep(): Step | undefined {
    return this.findPreviousStep();
  }

  skipSeatsSelection(): void {
    this.seatSelectionType.next(SeatSelectionType.TICKETS_FIRST);
    // The flow is swapped here rather than left to the wrapper template's `seatSelectionType$`
    // subscription: `setStepSkipped` only reaches steps of the *current* flow, so skipping before
    // the swap silently did nothing on a seats-first POS.
    this.setTicketsSteps(SeatSelectionType.TICKETS_FIRST);
    this.setStepSkipped(SeatsSelectionByTicketsStep, true);
  }

  private findNextStep(): Step {
    const currentStepIndex = this.currentStepIndex();
    // The route is not a step of this flow — `subs-order/:orderId` above all, which ends in an order
    // id. Stepping "forward" from there would start at index 0 and throw the user back to the very
    // first step of a flow they have already paid for.
    if (currentStepIndex === -1) {
      return null;
    }
    for (let i = currentStepIndex + 1; i < this.steps.length; i++) {
      if (!this.steps[i].skipped) {
        return this.steps[i];
      }
    }
    return null;
  }

  private findPreviousStep(): Step {
    const currentStepIndex = this.currentStepIndex();
    if (currentStepIndex === -1) {
      return null;
    }
    for (let i = currentStepIndex - 1; i >= 0; i--) {
      if (!this.steps[i].skipped) {
        return this.steps[i];
      }
    }
    return null;
  }

  public findCurrentStep(): Step {
    const currentStepIndex = this.currentStepIndex();
    return currentStepIndex === -1 ? null : this.steps[currentStepIndex];
  }

  private currentStepIndex(): number {
    return this.steps.findIndex((step) => step.route === this.getStepRoute());
  }

  setError(error: ResponseError | string): void {
    if (typeof error === 'string') {
      this.error.next(error);
    } else {
      const info = error?.error?.error?.info;
      this.error.next(Array.isArray(info) ? info[0]?.kind : info?.kind);
    }
  }

  getStepRoute(): string {
    return window.location.href.split('/').pop().split('?')[0];
  }
}

/** What the checkout needs out of a POS read, the shop it belongs to included. */
export interface PosContext {
  seatSelectionType: SeatSelectionType;
  theme: AppTheme;
  seatsLimit: number;
  orderTimeLimit: number;
  shopId: string;
  shop: Shop;
  productIds: string[];
  disableSeatsValidation: boolean;
  allowReservations: boolean;
  posType: POSType;
  faviconLight: Media;
  faviconDark: Media;
}
